import React from 'react';
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { AlertCircle, TrendingUp, BarChart3, Target } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks';

interface KeywordListProps {
  keywords?: string;
  type: string;
  icon: React.ElementType;
}

const KeywordList = ({ keywords = '', type, icon: Icon }: KeywordListProps) => {
  const keywordArray = keywords?.split(',')
    .map(keyword => keyword.trim())
    .filter(keyword => keyword.length > 0);  //


  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-blue-500" />
        <h4 className="text-sm font-semibold text-gray-800">{type}</h4>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {keywordArray.map((keyword, idx) => (
          <Badge
            key={idx}
            variant="secondary"
            className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-xs"
          >
            {keyword}
          </Badge>
        ))}
      </div>
    </div>
  );
};

interface SEOKeywordsSectionProps {
  seo_head_keywords?: string;
  seo_body_keywords?: string;
  seo_long_tail_keywords?: string;
}

const SEOKeywordsSection = ({
  seo_head_keywords = '',
  seo_body_keywords = '',
  seo_long_tail_keywords = '',
}: SEOKeywordsSectionProps) => {
  const { t } = useTranslation();

  const hasAnyKeywords = [seo_head_keywords, seo_body_keywords, seo_long_tail_keywords]
    .some(keywords => keywords?.trim().length > 0);

 if (!hasAnyKeywords) return null;

  return (
    <Card className="mt-4">
      <CardContent className="p-4">
        <div className="flex items-start gap-2 mb-4">
          <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t('seoKeywordsSection.seo_keywords_analysis')}</h3>
            <p className="text-xs text-gray-600">{t('seoKeywordsSection.optimized_keywords_description')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KeywordList
            keywords={seo_head_keywords}
            type={t('seoKeywordsSection.primary_focus_keywords')}
            icon={Target}
          />
          <KeywordList
            keywords={seo_body_keywords}
            type={t('seoKeywordsSection.high_traffic_keywords')}
            icon={TrendingUp}
          />
          {seo_long_tail_keywords && (
            <KeywordList
              keywords={seo_long_tail_keywords}
              type={t('seoKeywordsSection.high_converting_keywords')}
              icon={BarChart3}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SEOKeywordsSection;